import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Printer, Download, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../hooks/useBranding';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const fmtMoney = (n) =>
  (typeof n === 'number' ? n : 0).toLocaleString('en-AU', {
    style: 'currency', currency: 'AUD', maximumFractionDigits: 0,
  });
const fmtInt = (n) => (typeof n === 'number' ? n : 0).toLocaleString('en-AU');
const fmtPct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n}%`);
const fmtDate = (s) => (s ? String(s).substring(0, 10) : '—');

/**
 * PrintReportDialog — user's request Feb 2026.
 *
 * Presents a range picker (Today / This week / This month / Custom),
 * fetches `/api/gameplan/print-report`, and opens a print-ready
 * `PrintReportView` inside a full-screen modal with `window.print`
 * and PDF download buttons. Uses a print-only stylesheet so the modal
 * chrome is stripped when printing.
 */
export default function PrintReportDialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [range, setRange] = useState('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (range === 'custom') {
        if (!from || !to) { toast.error('Pick a from and to date'); setLoading(false); return; }
        params.append('from', from); params.append('to', to);
      }
      const r = await fetch(`${backendUrl}/api/gameplan/print-report?${params.toString()}`, { headers: auth });
      if (!r.ok) throw new Error(await r.text());
      setReport(await r.json());
    } catch (e) { toast.error(`Report failed — ${e.message}`); }
    finally { setLoading(false); }
  };

  const handlePrint = () => window.print();

  return (
    <>
      <Dialog open={open && !report} onOpenChange={(v) => { onOpenChange(v); if (!v) setReport(null); }}>
        <DialogContent className="max-w-lg" data-testid="print-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-4 h-4" /> Print performance report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 block">Reporting period</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'today',  label: 'Today' },
                  { key: 'week',   label: 'This week' },
                  { key: 'month',  label: 'This month' },
                  { key: 'custom', label: 'Custom range' },
                ].map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setRange(r.key)}
                    data-testid={`report-range-${r.key}`}
                    className={`text-sm border rounded-lg px-3 py-2 text-left transition ${
                      range === r.key
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200'
                    }`}
                  >
                    <Calendar className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {range === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">From</Label>
                  <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from-date" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">To</Label>
                  <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to-date" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="report-cancel">Cancel</Button>
            <Button onClick={fetchReport} disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="report-generate">
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Printer className="w-3 h-3 mr-1" />}
              Generate report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {report && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto print-report-modal" data-testid="print-report-view">
          <div className="sticky top-0 z-10 bg-white border-b flex items-center justify-between px-6 py-3 no-print">
            <div className="text-sm text-slate-600">
              Performance report — <b>{report.period.from}</b> to <b>{report.period.to}</b>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handlePrint} size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="report-print">
                <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save as PDF
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setReport(null); onOpenChange(false); }} data-testid="report-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <PrintReportView report={report} />
        </div>
      )}
    </>
  );
}

// ── The printable report itself ──────────────────────────────────
function PrintReportView({ report }) {
  const brand = useBranding();
  const biz = brand.business_name || 'DerwentAI';
  const t = report.totals;
  const p = report.previous_totals;
  const d = report.deltas_pct;

  return (
    <div className="max-w-[900px] mx-auto p-8 print-content text-slate-800">
      <style>{`
        /* Screen — glossy modal shell */
        .kpi-card { padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 12px; }
        .kpi-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; }
        .kpi-value { font-size: 20px; font-weight: 700; color: #0f172a; margin-top: 4px; }
        .kpi-delta { font-size: 11px; margin-top: 4px; }
        .kpi-delta.up { color: #059669; } .kpi-delta.down { color: #dc2626; }
        table.rpt { width: 100%; border-collapse: collapse; font-size: 11px; }
        table.rpt th, table.rpt td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
        table.rpt th { background: #f8fafc; text-transform: uppercase; letter-spacing: 0.04em; font-size: 9px; color: #475569; }
        table.rpt tr:nth-child(even) td { background: #fbfbfc; }
        .section-title { font-size: 14px; font-weight: 800; margin: 22px 0 8px; color: #0f172a; letter-spacing: -0.01em; }
        .subtle { color: #64748b; font-size: 11px; }
        .bar { height: 10px; background: #0f172a; border-radius: 3px; }
        .kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }

        /* ── Print — A4, page numbers, no chrome, sturdy tables ── */
        @page {
          size: A4;
          margin: 14mm 12mm 18mm 12mm;
          @bottom-center { content: "${biz} Performance Report · Page " counter(page) " of " counter(pages); font-size: 9px; color: #64748b; }
        }
        @media print {
          /* Hide everything on the page, then re-show only the report modal.
             This is the classic print-modal technique — nothing under the
             modal (Game Plan Tracker toolbar, sidebar, etc.) leaks through. */
          body * { visibility: hidden !important; }
          .print-report-modal, .print-report-modal * { visibility: visible !important; }
          .print-report-modal .no-print, .print-report-modal .no-print * { visibility: hidden !important; }

          .print-report-modal {
            position: absolute !important;
            left: 0 !important; top: 0 !important; right: auto !important; bottom: auto !important;
            width: 100% !important; height: auto !important;
            overflow: visible !important;
            background: #fff !important;
            z-index: auto !important;
          }
          body { background: #fff !important; margin: 0 !important; }
          html, body { height: auto !important; overflow: visible !important; }
          .print-content {
            max-width: 100% !important; padding: 0 !important; margin: 0 !important;
            color: #0f172a !important;
            font-size: 10.5px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* KPI grid — Tailwind classes drop out in print, so anchor to .kpi-grid */
          .kpi-grid { display: grid !important; grid-template-columns: repeat(4, 1fr) !important; gap: 8px !important; margin-bottom: 12px !important; }
          .kpi-card { padding: 8px 10px !important; border: 1px solid #cbd5e1 !important; border-radius: 8px !important; break-inside: avoid; }
          .kpi-value { font-size: 15px !important; }
          .kpi-label { font-size: 8px !important; }
          .kpi-delta { font-size: 9px !important; }
          /* Tables — never break a row across pages, repeat headers */
          table.rpt { font-size: 9px !important; page-break-inside: auto; }
          table.rpt thead { display: table-header-group; }
          table.rpt tfoot { display: table-footer-group; }
          table.rpt tr { page-break-inside: avoid; page-break-after: auto; }
          table.rpt th { background: #f1f5f9 !important; color: #334155 !important; font-size: 8px !important; padding: 4px 5px !important; }
          table.rpt td { padding: 4px 5px !important; }
          table.rpt tr:nth-child(even) td { background: #fafafa !important; }
          .section-title { font-size: 12px !important; margin: 16px 0 6px !important; page-break-after: avoid; }
          .print-page-break { page-break-before: always; }
          .print-cover { page-break-after: always; }
          .bar { background: #334155 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .brand-band { background: linear-gradient(90deg, #0f172a 0%, #1e293b 100%) !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          h1 { font-size: 24px !important; }
          .subtle { font-size: 9px !important; }
          /* Avoid orphan headings just before a page break */
          h2, h3, .section-title { break-after: avoid; }
        }
      `}</style>

      {/* ── Cover / letterhead — prints as page 1, then page-breaks ── */}
      <div className="print-cover">
        <div className="brand-band" style={{
          background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)',
          color: '#fff', borderRadius: 12, padding: '20px 24px',
          marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.75 }}>
              {biz} · Game Plan Tracker
            </div>
            <h1 style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-0.02em', margin: '6px 0 2px', color: '#ffffff' }}>Performance Report</h1>
            <div style={{ fontSize: 12, opacity: 0.9 }}>
              {report.period.from} → {report.period.to}
              &nbsp;·&nbsp; vs {report.previous.from} → {report.previous.to}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 10, opacity: 0.85, lineHeight: 1.5 }}>
            <div>Generated by <b>{report.meta.generated_by}</b></div>
            <div>{new Date(report.meta.generated_at).toLocaleString('en-AU')}</div>
            <div style={{ marginTop: 4 }}>Est. revenue @ {(report.meta.commission_rate * 100).toFixed(2)}% upfront</div>
          </div>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="section-title">Headline metrics for the period</div>
      <div className="kpi-grid">
        <Kpi label="Applications lodged"  value={fmtInt(t.applications_lodged)}     delta={d.applications_lodged} />
        <Kpi label="Loan $ lodged"        value={fmtMoney(t.loan_amount_lodged)}    delta={d.loan_amount_lodged} />
        <Kpi label="Est. revenue"         value={fmtMoney(t.estimated_revenue)}     delta={d.estimated_revenue} />
        <Kpi label="Applications approved" value={fmtInt(t.applications_approved)}  delta={d.applications_approved} />
        <Kpi label="Approved $"           value={fmtMoney(t.approved_loan_amount)}  delta={d.approved_loan_amount} />
        <Kpi label="Settled"              value={fmtInt(t.settled_count)}           />
        <Kpi label="Avg loan size"        value={fmtMoney(t.avg_loan_size)} />
        <Kpi label="Lodged→Approved"      value={`${t.conversion_lodged_to_approved}%`} />
      </div>

      {/* ── Broker performance ── */}
      <div className="section-title">Broker &amp; team member performance</div>
      <table className="rpt">
        <thead><tr>
          <th>Broker</th><th>Lodged</th><th>Loan $</th><th>Approved</th><th>Approved $</th>
          <th>Settled</th><th>Est. rev</th><th>Conv %</th><th>Avg $</th>
        </tr></thead>
        <tbody>
          {report.broker_performance.map((r) => (
            <tr key={r.broker}>
              <td className="font-semibold">{r.broker}</td>
              <td>{fmtInt(r.lodged)}</td>
              <td>{fmtMoney(r.loan_amount)}</td>
              <td>{fmtInt(r.approved)}</td>
              <td>{fmtMoney(r.approved_amount)}</td>
              <td>{fmtInt(r.settled)}</td>
              <td>{fmtMoney(r.revenue)}</td>
              <td>{r.conversion_rate}%</td>
              <td>{fmtMoney(r.avg_loan_size)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Lender breakdown ── */}
      <div className="section-title">Lender breakdown</div>
      <table className="rpt">
        <thead><tr><th>Lender</th><th>Deals</th><th>Lodged $</th><th>Approved $</th><th style={{width: 200}}>Share</th></tr></thead>
        <tbody>
          {report.lender_breakdown.map((r) => {
            const share = t.applications_lodged ? Math.round(100.0 * r.count / t.applications_lodged) : 0;
            return (
              <tr key={r.lender}>
                <td className="font-semibold">{r.lender}</td>
                <td>{fmtInt(r.count)}</td>
                <td>{fmtMoney(r.amount)}</td>
                <td>{fmtMoney(r.approved_amount)}</td>
                <td><div className="bar" style={{ width: `${share}%` }} /><span className="subtle">{share}%</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Customer table ── */}
      <div className="section-title print-page-break">Customers ({report.customers.length})</div>
      <table className="rpt">
        <thead><tr>
          <th>Client</th><th>Broker</th><th>Lender</th><th>Purpose</th><th>Source</th>
          <th>Submitted</th><th>Loan $</th><th>Approved $</th><th>Stage</th><th>Est. rev</th>
        </tr></thead>
        <tbody>
          {report.customers.map((r, i) => (
            <tr key={i}>
              <td className="font-semibold">{r.client_name || '—'}</td>
              <td>{r.broker || '—'}</td>
              <td>{r.lender || '—'}</td>
              <td>{r.loan_type || '—'}</td>
              <td>{r.lead_source || '—'}</td>
              <td>{fmtDate(r.submitted_date)}</td>
              <td>{fmtMoney(r.est_loan_amount)}</td>
              <td>{fmtMoney(r.approved_amount)}</td>
              <td>{r.status_stage || '—'}</td>
              <td>{fmtMoney(r.est_revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Deals in progress ── */}
      <div className="section-title print-page-break">Deals currently in progress ({report.in_progress.length})</div>
      <table className="rpt">
        <thead><tr><th>Client</th><th>Broker</th><th>Lender</th><th>Stage</th><th>Submitted</th><th>Loan $</th><th>Last change</th></tr></thead>
        <tbody>
          {report.in_progress.slice(0, 80).map((r, i) => (
            <tr key={i}>
              <td className="font-semibold">{r.client_name || '—'}</td>
              <td>{r.broker || '—'}</td>
              <td>{r.lender || '—'}</td>
              <td>{r.stage || '—'}</td>
              <td>{fmtDate(r.submitted)}</td>
              <td>{fmtMoney(r.est_loan_amount)}</td>
              <td className="subtle">{fmtDate(r.last_change)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {report.in_progress.length > 80 && <div className="subtle mt-1">…{report.in_progress.length - 80} more</div>}

      {/* ── Stalled ── */}
      <div className="section-title">Deals requiring action — stalled &gt; 10 days ({report.stalled.length})</div>
      <table className="rpt">
        <thead><tr><th>Client</th><th>Broker</th><th>Lender</th><th>Stage</th><th>Loan $</th><th>Days idle</th></tr></thead>
        <tbody>
          {report.stalled.slice(0, 50).map((r, i) => (
            <tr key={i}>
              <td className="font-semibold">{r.client_name || '—'}</td>
              <td>{r.broker || '—'}</td>
              <td>{r.lender || '—'}</td>
              <td>{r.stage || '—'}</td>
              <td>{fmtMoney(r.est_loan_amount)}</td>
              <td style={{ color: '#dc2626', fontWeight: 700 }}>{r.days_since_change}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Comparison charts ── */}
      <div className="section-title print-page-break">Daily, weekly &amp; monthly comparisons</div>
      <ComparisonBlock title="Daily (last 30 days)"   series={report.series.daily} />
      <ComparisonBlock title="Weekly (last 12 weeks)" series={report.series.weekly} />
      <ComparisonBlock title="Monthly (last 12 months)" series={report.series.monthly} />

      <div className="subtle text-center mt-8 pt-4 border-t">
        Report generated by {biz} Game Plan Tracker · Est. revenue calculated at {(report.meta.commission_rate * 100).toFixed(2)}% upfront commission
      </div>
    </div>
  );
}

function Kpi({ label, value, delta }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {delta != null && (
        <div className={`kpi-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : ''}`}>
          {fmtPct(delta)} vs previous period
        </div>
      )}
    </div>
  );
}

function ComparisonBlock({ title, series }) {
  if (!series || series.length === 0) return (
    <div className="mb-4"><div className="text-xs font-semibold mb-1">{title}</div><div className="subtle">No data in this window.</div></div>
  );
  const max = Math.max(...series.map((s) => s.amount || 0), 1);
  return (
    <div className="mb-5">
      <div className="text-xs font-semibold mb-2">{title}</div>
      <table className="rpt">
        <thead><tr><th>Period</th><th>Lodged</th><th>Approved</th><th>Loan $</th><th style={{ width: 240 }}>&nbsp;</th></tr></thead>
        <tbody>
          {series.map((s) => (
            <tr key={s.period}>
              <td className="font-mono text-[10px]">{s.period}</td>
              <td>{fmtInt(s.lodged)}</td>
              <td>{fmtInt(s.approved)}</td>
              <td>{fmtMoney(s.amount)}</td>
              <td><div className="bar" style={{ width: `${(s.amount / max) * 100}%` }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
